import React, { useState } from 'react';
import { Quote, TaskInput, MaterialInput } from '../types/quote';
import { saveQuoteDraft, sendQuote } from '../api/quote';
import QuoteLineItemsEditor from './QuoteLineItemsEditor';

interface QuoteBuilderProps {
  quote: Quote;
  onSaved: (quote: Quote) => void;
  onSent: (quote: Quote) => void;
}

function toTaskInputs(quote: Quote): TaskInput[] {
  if (quote.tasks.length === 0) return [{ description: '', resources: [{ description: '', ratePerHour: 0, hours: 0 }] }];
  return quote.tasks.map((t) => ({
    description: t.description,
    resources: t.resources.map((r) => ({
      description: r.description,
      ratePerHour: r.ratePerHour,
      hours: r.hours,
      resourceCatalogueItemId: r.resourceCatalogueItemId,
    })),
  }));
}

function toMaterialInputs(quote: Quote): MaterialInput[] {
  return quote.materials.map((m) => ({ description: m.description, quantity: m.quantity, pricePerUnit: m.pricePerUnit }));
}

const QuoteBuilder: React.FC<QuoteBuilderProps> = ({ quote, onSaved, onSent }) => {
  const [tasks, setTasks] = useState<TaskInput[]>(() => toTaskInputs(quote));
  const [materials, setMaterials] = useState<MaterialInput[]>(() => toMaterialInputs(quote));
  const [isVatRegistered, setIsVatRegistered] = useState(quote.isVatRegistered);
  const [vatRate, setVatRate] = useState(quote.vatRate);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildPayload = () => ({
    isVatRegistered,
    vatRate,
    tasks: tasks
      .filter((t) => t.description.trim() !== '')
      .map((t) => ({ ...t, resources: t.resources.filter((r) => r.description.trim() !== '') })),
    materials: materials.filter((m) => m.description.trim() !== ''),
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await saveQuoteDraft(quote.id, buildPayload());
      onSaved(updated);
    } catch (e: any) {
      setError(e.response?.data ?? e.message ?? 'Failed to save Quote.');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      await saveQuoteDraft(quote.id, buildPayload());
      const sent = await sendQuote(quote.id);
      onSent(sent);
    } catch (e: any) {
      setError(e.response?.data ?? e.message ?? 'Failed to send Quote.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 space-y-4 bg-surface-container-low border-b border-outline-variant">
      <div className="flex items-center justify-between">
        <h3 className="font-title-md text-on-surface">Build your Quote</h3>
        {error && <span className="text-xs text-error">{error}</span>}
      </div>

      <QuoteLineItemsEditor
        tasks={tasks}
        materials={materials}
        isVatRegistered={isVatRegistered}
        vatRate={vatRate}
        onTasksChange={setTasks}
        onMaterialsChange={setMaterials}
        onVatChange={(v, r) => {
          setIsVatRegistered(v);
          setVatRate(r);
        }}
      />

      <div className="flex items-center gap-3">
        <button
          onClick={handleSaveDraft}
          disabled={saving || sending}
          className="px-4 py-1.5 rounded-lg text-sm border border-outline-variant text-on-surface hover:bg-surface-container disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        <button
          onClick={handleSend}
          disabled={saving || sending}
          className="px-4 py-1.5 rounded-lg text-sm bg-primary text-on-primary hover:bg-primary/90 disabled:opacity-50"
        >
          {sending ? 'Sending...' : 'Send Quote'}
        </button>
      </div>
    </div>
  );
};

export default QuoteBuilder;
